import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/api";
import { getQueryKey } from "@key.ai/constants";
import { cleanObject } from "@/lib/helper";
import qnaClientApi from "@/api-client/qna-client";

interface ApiResponsePagination<T> {
  data: T[];
  count: number;
  pageParam: number;
  reactions?: any[];
  items?: T[];
}

interface ReactionData {
  _id: string;
  emoji: string;
  count: number;
}

interface UserReactionData {
  id: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
  optimized_image?: {
    webp: string;
    heif: string;
  };
}

interface FetchPageParams {
  pageParam?: number;
}

interface EventQnAReactionsParams {
  eventId: string;
  commentId: string;
  enabled: boolean | string;
}

interface EventQnAUsersReactionsParams extends EventQnAReactionsParams {
  reactionId: string;
  reactionsSuccess: boolean;
}

type ExtendedApiResponsePagination<T> = ApiResponsePagination<T> & {
  totalRecords?: number;
  approvedQuestionsCount?: number;
  allQuestionsCount?: number;
  answeredQuestionsCount?: number;
  archivedQuestionsCount?: number;
  pendingQuestionsCount?: number;
};
export const useEventQnA = ({
  eventId,
  questionStatus,
  isAnswered,
  addedQuestion,
  isArchived,
  isBookmarked,
  isPending,
  sortBy,
  search
}: {
  eventId: string;
  questionStatus?: string;
  addedQuestion?: string[];
  isAnswered?: boolean;
  isArchived?: boolean;
  isPending?: boolean;
  isBookmarked?: boolean;
  sortBy?: string;
  search?: string;
}) => {
  const queryKey = getQueryKey("EVENT_QNA_LIST");

  const fetchQuestions = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await qnaClientApi
      .get(`${eventId}/questions`, {
        searchParams: cleanObject({
          page: pageParam as string,
          limit: 10,
          status: questionStatus,
          isAnswered,
          sortBy,
          isArchived,
          isBookmarked,
          isPending,
          search
        })
      })
      .json<ApiResponse<any>>();
    const newData = {
      ...data,
      items: data.items.filter((item: any) =>
        addedQuestion ? !addedQuestion.includes(item._id) : true
      )
    };
    return {
      ...newData,
      pageParam
    };
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetching,
    refetch
  } = useInfiniteQuery<ExtendedApiResponsePagination<any>>({
    queryKey: [queryKey, eventId, sortBy, search],
    queryFn: fetchQuestions,
    getNextPageParam: (lastPage) => {
      if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
        return lastPage?.pageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  });

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetching,
    refetch
  };
};

export const useQuestionComments = ({
  eventId,
  questionId
}: {
  eventId: string;
  questionId: string;
}) => {
  const queryKey = getQueryKey("EVENT_QUESTION_COMMENTS");

  const fetchComments = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await qnaClientApi
      .get(`${eventId}/questions/${questionId}/comments`, {
        searchParams: cleanObject({
          page: pageParam as string,
          limit: 10
        })
      })
      .json<ApiResponse<any>>();

    return { ...data, pageParam };
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    refetch,
    isFetching
  } = useInfiniteQuery<ApiResponsePagination<any>>({
    queryKey: [queryKey, questionId],
    queryFn: fetchComments,
    getNextPageParam: (lastPage) => {
      if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
        return lastPage?.pageParam + 1;
      }
      return undefined;
    },
    enabled: !!eventId && !!questionId,
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  });

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetching,
    refetch
  };
};

export const useEventQnASettings = ({ eventId }: { eventId: string }) => {
  const queryKey = getQueryKey("EVENT_QNA_SETTINGS");

  const fetchSettings = async () => {
    const { data } = await qnaClientApi.get(`qna/${eventId}`).json<ApiResponse<any>>();
    return data;
  };

  return useQuery<any>({
    queryKey: [queryKey, eventId],
    queryFn: fetchSettings,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    // refetchOnMount: true,
    enabled: !!eventId
  });
};

export const useEventQnAStats = ({ eventId }: { eventId: string }) => {
  const queryKey = getQueryKey("EVENT_QNA_STATS");
  const fetchStats = async () => {
    const { data } = await qnaClientApi.get(`qna/${eventId}/stats`).json<ApiResponse<any>>();
    return data;
  };
  return useQuery<any>({
    queryKey: [queryKey, eventId],
    queryFn: fetchStats,
    refetchInterval: false,
    enabled: !!eventId,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });
};

export const useEventQnAReactions = ({
  eventId,
  questionId,
  commentId,
  enabled
}: {
  eventId: string;
  questionId: string;
  commentId: string;
  enabled: boolean | string;
}) => {
  const queryKey = getQueryKey("EVENTS_QNA_REACTIONS");

  const fetchReactions = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await qnaClientApi
      .get(`${eventId}/questions/${questionId}/comments/${commentId}/reactions`, {
        searchParams: cleanObject({
          page: pageParam as string,
          limit: 10
        })
      })
      .json<ApiResponse<any>>();
    return { ...data, pageParam };
  };
  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    refetch
  } = useInfiniteQuery<ApiResponsePagination<any>>({
    queryKey: [queryKey, eventId, commentId],
    queryFn: fetchReactions,
    getNextPageParam: (lastPage) => {
      if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
        return lastPage?.pageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    isFetching,
    isSuccess: status === "success",
    refetch,
    enabled
  };
};

export const useEventQnAUsersReactions = ({
  eventId,
  questionId,
  commentId,
  reactionId,
  enabled,
  reactionsSuccess
}: {
  eventId: string;
  questionId: string;
  commentId: string;
  reactionId: string;
  enabled: boolean | string;
  reactionsSuccess: boolean;
}) => {
  const queryKey = getQueryKey("EVENTS_QNA_USERS_REACTIONS");

  const fetchUsersReactions = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await qnaClientApi
      .get(`${eventId}/questions/${questionId}/comments/${commentId}/reactions/${reactionId}`, {
        searchParams: cleanObject({
          page: pageParam as string,
          limit: 10
        })
      })
      .json<ApiResponse<any>>();
    return { ...data, pageParam };
  };
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isLoading, refetch } =
    useInfiniteQuery<ApiResponsePagination<any>>({
      queryKey: [queryKey, eventId, commentId, reactionId],
      queryFn: fetchUsersReactions,
      getNextPageParam: (lastPage) => {
        if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
          return lastPage?.pageParam + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnMount: true
    });
  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isSuccess: status === "success",
    isLoading,
    refetch,
    enabled
  };
};

// Define the response structure
interface QuestionCountResponse {
  status: number;
  message: string;
  data: {
    allQuestionsCount: number;
    answeredQuestionsCount: number;
    approvedQuestionsCount: number;
    archivedQuestionsCount: number;
    pendingQuestionsCount: number;
    total: number;
  };
}

// Props for the hook
interface UseEventQuestionCountProps {
  eventId: string;
  enabled?: boolean;
}

export const useEventQuestionCount = ({ eventId, enabled = true }: UseEventQuestionCountProps) => {
  const queryKey = getQueryKey("EVENT_QNA_QUESTION_COUNT");

  const fetchQuestionCount = async (): Promise<QuestionCountResponse["data"]> => {
    const { data } = await qnaClientApi
      .get(`${eventId}/questions/count`)
      .json<QuestionCountResponse>();
    return data;
  };

  return useQuery<QuestionCountResponse["data"]>({
    queryKey: [queryKey, eventId],
    queryFn: fetchQuestionCount,
    enabled: !!eventId && enabled,
    refetchOnWindowFocus: false
  });
};
